import { AIConnection } from "@/lib/ai/ai-connection"
import { CullerConnections } from "@/lib/culling/culler-connection"
import {
	MetadataLoaderConnection,
	PreviewLoaderConnection,
} from "@/lib/metadata/metadata-loader-connection"
import { Sentry } from "@/telemetry/sentry"
import { wait } from "@/utils/wait"
import { editClusteringImporter } from "../importing/edit-clustering-importing"
import { metadataImporter } from "../importing/metadata-importing"
import { previewImporter } from "../importing/preview-importing"
import { previewQueue, processingQueue } from "../processing/processing-queue"
import type { PhotoExportingSession } from "./photo-exporting-session"

type PhotoOutputFlow = "export" | "publish"

const flowLabel: Record<PhotoOutputFlow, string> = {
	export: "Export",
	publish: "Publish",
}

async function pauseQueuesBeforeWorkerReset(flow: PhotoOutputFlow) {
	try {
		await Promise.all([
			metadataImporter.pauseAndWaitForInFlightTasksToComplete(),
			editClusteringImporter.pauseAndWaitForInFlightTasksToComplete(),
		])
	} catch (e) {
		console.warn(`[${flowLabel[flow]}] queue drain before reset failed (continuing):`, e)
		Sentry.captureException(e, { tags: { step: `${flow}PreResetQueues` } })
	}
}

async function resetWorkersBeforePhotoOutput(flow: PhotoOutputFlow) {
	try {
		await Promise.all([MetadataLoaderConnection.reset(), PreviewLoaderConnection.reset()])
		await Promise.all([
			...CullerConnections.map((connection) => connection.reset()),
			AIConnection.reset(),
		])
	} catch (e) {
		console.warn(`[${flowLabel[flow]}] worker reset failed (continuing):`, e)
		Sentry.captureException(e, { tags: { step: `${flow}PreResetWorkers` } })
	}
}

export async function preparePhotoOutputWorkers(
	flow: PhotoOutputFlow,
	session: PhotoExportingSession,
) {
	// Export and publish both render through the editor, so keep worker/queue setup identical.
	// Pause producers together; preview cancellation settles queued callers, drains
	// active renders, and releases the AI editor lock before resetting its worker.
	await Promise.all([
		processingQueue.pauseQueueForReason(flow, { waitForInFlight: true }),
		previewQueue.pauseQueueForReason(flow, { waitForInFlight: true }),
		previewImporter.pause(),
		pauseQueuesBeforeWorkerReset(flow),
	])
	session.throwIfCanceled()

	await resetWorkersBeforePhotoOutput(flow)
	session.throwIfCanceled()

	await session.waitUnlessCanceled(wait(500))
}

export function cleanupPhotoOutputWorkers() {
	processingQueue.resumeQueueForReason("export")
	processingQueue.resumeQueueForReason("publish")
	previewQueue.resumeQueueForReason("export")
	previewQueue.resumeQueueForReason("publish")
	previewImporter.resumeIfNeeded()
	metadataImporter.resumeIfNeeded()
	editClusteringImporter.resumeIfNeeded()
}
